import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Typography,
    Stack,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    Skeleton,
    Paper,
} from "@mui/material";
import { intl, DmsPage, DmsAltBox, DmsTable } from "dmsbasereact";
import { ReportFilters } from "./ReportFilters";
import { useReport } from "./useReport";

/**
 * TableSkeleton - Shows a skeleton loading state for tables
 */
const TableSkeleton = ({ columns, rowCount = 10 }) => {
    return (
        <Paper sx={{ overflow: "auto", boxShadow: "none" }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        {columns.map((col, i) => (
                            <TableCell
                                key={i}
                                sx={{
                                    width: col.width || "auto",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    padding: "8px 16px",
                                }}
                            >
                                <Skeleton
                                    variant="text"
                                    animation="wave"
                                    width="80%"
                                    height={20}
                                />
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.from({ length: rowCount }).map((_, rowIdx) => (
                        <TableRow key={rowIdx}>
                            {columns.map((col, colIdx) => (
                                <TableCell
                                    key={colIdx}
                                    sx={{ padding: "8px 16px" }}
                                >
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        width={`${60 + Math.random() * 30}%`}
                                        height={18}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
};

/**
 * ReportLayout - Generic report layout component
 *
 * Eliminates code duplication across report pages by providing:
 * - Filter state management via useReport hook
 * - Pre-filter building for DmsTable
 * - Export URL building
 * - Status formatting from backend
 */
export const ReportLayout = ({
    title,
    titleDefault,
    manualSlug = null,
    tableUrl,
    filterOptionsUrl = null,
    exportUrl = null,
    header: headerConfig,
    filterFields: filterFieldsConfig,
    dateFilterLabel = null,
    initialFilters = null,
}) => {
    const {
        state: { tableRef, filters, clearKey, filterFields, statusMap, columnVisibility },
        handlers: {
            setFilters,
            handleApplyFilters,
            handleClearFilters,
            handleRefresh,
            buildPreFilter,
            buildExportUrl,
            setColumnVisibility,
        },
    } = useReport({ filterOptionsUrl, exportUrl, filterFields: filterFieldsConfig, initialFilters });

    // State for initial loading skeleton
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Reset loading state when clearKey changes (filters applied/cleared)
    useEffect(() => {
        setIsInitialLoad(true);
        // Short delay to allow skeleton to be visible before table takes over
        const timer = setTimeout(() => {
            setIsInitialLoad(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [clearKey]);

    const header = useMemo(() => {
        return headerConfig.map((col) => {
            if (col.column_name === "status" && !col.function) {
                return {
                    ...col,
                    function: (value) => statusMap[value] || value || "--",
                };
            }
            return col;
        });
    }, [headerConfig, statusMap]);

    const visibleHeader = useMemo(() => {
        return header.filter((col) => columnVisibility[col.column_name] !== false);
    }, [header, columnVisibility]);

    const pageTitle = intl.get(title).d(titleDefault);

    return (
        <DmsPage name={pageTitle} manual_slug={manualSlug}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                            {pageTitle}
                        </Typography>
                        <ReportFilters
                            filterFields={filterFields}
                            filters={filters}
                            dateFilterLabel={dateFilterLabel}
                            onFiltersChange={setFilters}
                            onApply={handleApplyFilters}
                            onClear={handleClearFilters}
                            onRefresh={handleRefresh}
                            exportUrl={buildExportUrl()}
                            columns={header}
                            columnVisibility={columnVisibility}
                            onColumnVisibilityChange={setColumnVisibility}
                        />
                    </Stack>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    {isInitialLoad ? (
                        <TableSkeleton columns={visibleHeader} rowCount={10} />
                    ) : (
                        <DmsTable
                            key={`table-${clearKey}`}
                            ref={tableRef}
                            header={visibleHeader}
                            url={tableUrl}
                            managed={true}
                            needs_authentication={true}
                            pre_filter={buildPreFilter()}
                            toolbar={<></>}
                        />
                    )}
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

ReportLayout.propTypes = {
    title: PropTypes.string.isRequired,
    titleDefault: PropTypes.string.isRequired,
    manualSlug: PropTypes.string,
    tableUrl: PropTypes.string.isRequired,
    filterOptionsUrl: PropTypes.string,
    exportUrl: PropTypes.string,
    header: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            column_name: PropTypes.string.isRequired,
            width: PropTypes.string,
            function: PropTypes.func,
        })
    ).isRequired,
    filterFields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.oneOf(["text", "select", "multiselect"]).isRequired,
            optionsKey: PropTypes.string,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.string.isRequired,
                    label: PropTypes.string.isRequired,
                })
            ),
        })
    ).isRequired,
    initialFilters: PropTypes.object,
};
